import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ParticipantSection from './ParticipantSection';

function ConversationSidebar({ conversation }) {
  const currentReceiverSlug = globalThis.Qwoted.CURRENT_SLUG;
  const currentReceiverProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Identify the viewer by stable profile id; slug is a fallback for id-less rows.
  const isViewer = (p) => (currentReceiverProfileId != null && p.id === currentReceiverProfileId)
    || p.slug === currentReceiverSlug;

  const [expandedParticipants, setExpandedParticipants] = useState(new Set());

  // The "important" person to auto-expand depends on the viewer's role:
  //   - Media or PR viewer on a message-source thread → the represented entity (Expert or Product)
  //   - Expert viewer on a message-source thread → the media participant
  //   - DM or other thread with no origin entity → the first participant who isn't the viewer
  const originExpert = conversation?.origin_expert;
  const originProduct = conversation?.origin_product;
  // The viewer is "the expert" only when they are the self-represented profile behind this thread.
  // Key on the stable profile id, not the mutable slug; the null guard avoids undefined===undefined.
  const viewerIsExpert = currentReceiverProfileId != null
    && originExpert?.self_represented_profile_id === currentReceiverProfileId;
  // Products can't be viewers, so the expert-viewer short-circuit only applies on expert origins.
  const originEntity = viewerIsExpert ? null : originExpert || originProduct;
  const allOnThread = [...(conversation?.conversation_senders || []), ...(conversation?.conversation_receivers || [])];
  let importantSlug;
  if (viewerIsExpert) {
    importantSlug = allOnThread.find((p) => p.type_of_profile === 'type_media')?.slug;
  } else if (originEntity) {
    importantSlug = originEntity.slug;
  } else {
    importantSlug = allOnThread.find((p) => !isViewer(p))?.slug;
  }

  // Reset accordion when conversation changes, auto-expanding the important person if present.
  useEffect(() => {
    setExpandedParticipants(importantSlug ? new Set([importantSlug]) : new Set());
  }, [conversation?.conversation_id, importantSlug]);

  if (!conversation) return null;

  // Sidebar shows everyone on the thread including the current user. The represented expert (on
  // message-source threads) is promoted to position 0; dedup collapses a self-rep expert who's
  // already in the participant list.
  const dedupeBySlug = (profiles) => {
    const seen = new Set();
    return profiles.filter((p) => {
      if (!p?.slug || seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
  };

  const rawParticipants = dedupeBySlug([
    ...(conversation.conversation_senders || []),
    ...(conversation.conversation_receivers || []),
  ]);
  // Promote whichever origin entity exists (Expert or Product) to position 0. Both have the same
  // JSONB shape from the PostgREST view, so the rest of the sidebar treats them uniformly.
  const promotableEntity = originExpert || originProduct;
  const uniqueParticipants = promotableEntity ? dedupeBySlug([promotableEntity, ...rawParticipants]) : rawParticipants;

  const soleParticipant = uniqueParticipants[0];

  const toggleParticipant = (slug) => {
    const newExpanded = new Set(expandedParticipants);
    if (newExpanded.has(slug)) {
      newExpanded.delete(slug);
    } else {
      newExpanded.clear(); // Only one expanded at a time
      newExpanded.add(slug);
    }
    setExpandedParticipants(newExpanded);
  };

  return (
    <div className="w-100 h-100 bg-white border-start overflow-auto">
      {uniqueParticipants.length > 1 ? (
        <div>
          {uniqueParticipants.map((profile, index) => (
            <div
              key={profile.slug}
              className={classnames({ 'border-bottom': index === uniqueParticipants.length - 1 })}
            >
              {/* Collapsible Header */}
              <div
                role="button"
                tabIndex={0}
                aria-expanded={expandedParticipants.has(profile.slug)}
                onClick={() => toggleParticipant(profile.slug)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleParticipant(profile.slug);
                  }
                }}
                className={classnames('p-3 fw-bold paragraph-sm d-flex align-items-center', {
                  'border-top': index > 0 && expandedParticipants.size === 0,
                  'border-top border-2': index > 0 && expandedParticipants.size > 0,
                })}
                style={{ cursor: 'pointer' }}
              >
                <i
                  className={`fa-solid fa-fw fa-chevron-${expandedParticipants.has(profile.slug) ? 'down' : 'right'} me-2`}
                />
                {profile.name}
              </div>

              {/* Expanded: Profile Card + Teammates + Pitches for this participant */}
              {expandedParticipants.has(profile.slug) && (
                <ParticipantSection profile={profile} conversation={conversation} showBorderBottom={false} />
              )}
            </div>
          ))}
        </div>
      ) : (
        soleParticipant && <ParticipantSection profile={soleParticipant} conversation={conversation} />
      )}
    </div>
  );
}

const participantShape = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  first_name: PropTypes.string,
  profile_image_url: PropTypes.string,
  title_at_workplace: PropTypes.string,
  type_of_profile: PropTypes.string,
});

ConversationSidebar.propTypes = {
  conversation: PropTypes.shape({
    conversation_receivers: PropTypes.arrayOf(participantShape),
    conversation_senders: PropTypes.arrayOf(participantShape),
    origin_expert: PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
      profile_image_url: PropTypes.string,
      self_represented_profile_id: PropTypes.number,
    }),
    origin_product: PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
      profile_image_url: PropTypes.string,
    }),
    origin_type: PropTypes.string,
    origin_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default ConversationSidebar;
